import { ECharts, EChartsOption } from 'echarts';
import { YAXisOption } from 'echarts/types/dist/shared';
import { XYChartDashoardItemFactoryOptions } from 'src/app/dashboards/common/items/xy-chart';
import {
    IQueryContext,
    isSplitApplied,
} from 'src/app/services/query-context/models/IQueryContext';
import { distinctValues } from '../array';
import { EChartsAxisHelper } from './echarts-axis-helper';
import { EChartsTooltipHelper } from './echarts-tooltip-helper';
import { LoggerService } from 'src/app/services/logger/logger.service';
import { ca, de } from 'date-fns/locale';

export type StackingMode = 'none' | 'stacked' | 'normalized';

/**
 * Toggles the stacking of series in an XY ECharts option object.
 *
 * @param stackingMode - The stacking mode to apply. Can be 'none', 'stacked', or 'normalized'.
 * @param option - The ECharts option object to modify.
 * @param echartsInstance - The ECharts instance to use for rendering.
 * @param initFactoryOptions - The factory options for the XY chart dashboard item.
 * @param queryContext - The query context to use for rendering.
 * @returns The modified ECharts option object with updated stacking configuration.
 * @throws Will throw an error if `option.series` is not defined or not an array.
 * @throws Will throw an error if `stackValue` is not 'none', 'stacked', or 'normalized'.
 */
export function applyStackingMode(
    stackingMode: StackingMode,
    option: EChartsOption,
    echartsInstance: ECharts,
    initFactoryOptions: XYChartDashoardItemFactoryOptions,
    queryContext: IQueryContext,
    postProcessCallback: () => void
): EChartsOption {
    // copy the option object
    const chartOption: EChartsOption = { ...option };

    // raise error if option.series is not defined
    if (!chartOption.series) {
        throw new Error('Option.series is not defined');
    }

    // raise error if option.series is not an array
    if (!Array.isArray(chartOption.series)) {
        throw new Error('Option.series is not an array');
    }

    if (stackingMode === 'none') {
        // remove stack property from all series
        chartOption.series.forEach((series: any) => {
            series.stack = null;
            if (series.type === 'line') {
                // remove areaStyle property if it exists
                delete series.areaStyle;
            }
        });
    }

    if (stackingMode !== 'none') {
        // if stacking mode is not none, add missing values
        const startTime = performance.now();
        prepareEchartsDatasetForStacking(chartOption, queryContext);
        const endTime = performance.now();
        console.debug(
            `prepareEchartsDatasetForStacking took ${
                endTime - startTime
            } milliseconds`
        );
    }

    switch (stackingMode) {
        case 'stacked':
            // add stack property to all series
            chartOption.series.forEach((series: any) => {
                // stack value is a layer on which series are stacked
                series.stack = 'stack';
                if (series.type === 'line') {
                    series.areaStyle = {};
                }
            });
            break;
        case 'normalized':
            // a list of all distinct encodings
            let xSeriesEncode: any = new Set<string>();
            const ySeriesEncode = new Set<string>();

            // normalize the data to 100%
            chartOption.series.forEach((series: any) => {
                series.stack = 'stack';
                if (series.type === 'line') {
                    series.areaStyle = {};
                }
                xSeriesEncode.add(series.encode.x);
                ySeriesEncode.add(structuredClone(series.encode.y));
            });

            // error if more than one xSeriesEncode
            if (xSeriesEncode.size !== 1) {
                throw new Error('Multiple or zero x encodings found');
            }
            xSeriesEncode = xSeriesEncode.values().next().value!;

            // normalize the data to 100%
            // add column to dataset for each normalized encoding (add suffix _normalized)
            // error if dataste not defined
            if (!chartOption.dataset) {
                throw new Error('Option.dataset is not defined');
            }

            if (!Array.isArray(chartOption.dataset)) {
                throw new Error('Option.dataset is not an array');
            }

            if (!chartOption.dataset[0].source) {
                throw new Error('Option.dataset[0].source is not defined');
            }

            if (!Array.isArray(chartOption.dataset[0].source)) {
                throw new Error('Option.dataset[0].source is not an array');
            }

            // calculate sum of each value of xSeriesEncode
            const xSum: { [key: string]: { [key: string]: number } } = {};
            chartOption.dataset[0].source.forEach((row: any) => {
                const xValue = row[xSeriesEncode];
                if (!xSum[xValue]) {
                    xSum[xValue] = {};
                }
                ySeriesEncode.forEach((yEncode) => {
                    if (!xSum[xValue][yEncode]) {
                        xSum[xValue][yEncode] = 0;
                    }
                    xSum[xValue][yEncode] += row[yEncode];
                });
            });

            // process dataset
            // add normalized column to dataset
            chartOption.dataset[0].source.forEach((row: any) => {
                const xValue = row[xSeriesEncode];
                ySeriesEncode.forEach((yEncode) => {
                    const normalizedValue =
                        row[yEncode] === null
                            ? null
                            : row[yEncode] / xSum[xValue][yEncode];
                    row[yEncode + '_normalized'] = normalizedValue;
                });
            });

            // replace ySeriesEncode with ySeriesEncode_normalized
            chartOption.series.forEach((series: any) => {
                series.encode = {
                    ...series.encode,
                    y: series.encode.y + '_normalized',
                };
                // delete tooltip property if it exists
                delete series.encode.tooltip;
            });

            let processTooltip = () => {
                // process tooltip
                // if (initFactoryOptions.tooltip) {
                if (chartOption.tooltip) {
                    chartOption.tooltip =
                        EChartsTooltipHelper.createDefaultTooltip(
                            echartsInstance,
                            {
                                xLabelFormatter:
                                    initFactoryOptions.tooltip?.xLabelFormatterGetter?.(
                                        queryContext
                                    ),
                                valueFormatter:
                                    EChartsTooltipHelper.tooltipPercentValueFormatter,
                            }
                        );
                }
            };
            processTooltip();

            let processYAxis = () => {
                // set y axis to percentage
                // stacking mode does not support multiple y Axes
                if (chartOption.yAxis && Array.isArray(chartOption.yAxis)) {
                    throw new Error('Multiple y Axes not supported');
                }

                if (!chartOption.yAxis) {
                    chartOption.yAxis = {};
                }

                chartOption.yAxis = {
                    ...chartOption.yAxis,
                    ...{
                        axisLabel: {
                            formatter:
                                EChartsAxisHelper.getAxisPercentLabelFormatter(
                                    0
                                ),
                        } as YAXisOption['axisLabel'],
                    },
                };
                // limit y axis to 100%
                chartOption.yAxis.max = 1;
            };
            processYAxis();
            break;
        default:
            throw new Error('Stacking mode not supported');
    }
    // call post process callback
    postProcessCallback();

    return chartOption;
}

export function prepareEchartsDatasetForStacking(
    chartOption: EChartsOption,
    queryContext: IQueryContext
) {
    // raise error if option.series is not defined
    if (!chartOption.series) {
        throw new Error('Option.series is not defined');
    }

    // raise error if option.series is not an array
    if (!Array.isArray(chartOption.series)) {
        throw new Error('Option.series is not an array');
    }

    // a list of all distinct encodings
    let xSeriesEncode: any = new Set<string>();
    const ySeriesEncode = new Set<string>();

    chartOption.series.forEach((series: any) => {
        xSeriesEncode.add(series.encode.x);
        ySeriesEncode.add(structuredClone(series.encode.y));
    });

    // error if more than one xSeriesEncode
    if (xSeriesEncode.size !== 1) {
        throw new Error('Multiple or zero x encodings found');
    }
    xSeriesEncode = xSeriesEncode.values().next().value!;
    // if split applied, add missing rows to dataset
    if (isSplitApplied(queryContext)) {
        const splitGroupValues: string[] = distinctValues(
            (chartOption.dataset as any)[0].source.map(
                (item: any) => item.split_group
            )
        );
        const xValues: string[] = distinctValues(
            (chartOption.dataset as any)[0].source.map(
                (item: any) => item[xSeriesEncode]
            )
        );

        // add row with xValue and all other properties as null for each missing pair of split group and x value
        splitGroupValues.forEach((splitGroup: string) => {
            xValues.forEach((xValue: string) => {
                if (
                    !(chartOption.dataset as any)[0].source.some(
                        (item: any) =>
                            item.split_group === splitGroup &&
                            item[xSeriesEncode] === xValue
                    )
                ) {
                    const newRow: any = structuredClone(
                        (chartOption.dataset as any)[0].source[0]
                    );
                    newRow[xSeriesEncode] = xValue;
                    newRow.split_group = splitGroup;
                    // for every property in newRow, set it to null
                    Object.keys(newRow).forEach((key) => {
                        if (key !== xSeriesEncode && key !== 'split_group') {
                            newRow[key] = null;
                        }
                    });
                    (chartOption.dataset as any)[0].source.push(newRow);
                }
            });
        });
        // sort the dataset by split group and xSeriesEncode
        (chartOption.dataset as any)[0].source.sort((a: any, b: any) => {
            if (a.split_group < b.split_group) {
                return -1;
            }
            if (a.split_group > b.split_group) {
                return 1;
            }
            if (a[xSeriesEncode] < b[xSeriesEncode]) {
                return -1;
            }
            if (a[xSeriesEncode] > b[xSeriesEncode]) {
                return 1;
            }
            return 0;
        });
    }
}
